import type { User } from 'firebase/auth';
import { ensureUserDocument, type EnsureUserDocPayload } from '../services';
import { getSignupDeviceInfo } from './deviceInfo';

type EnsureAuthUserDocOptions = {
  provider?: string;
  source?: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  includeDeviceInfo?: boolean;
};

const clean = (value: unknown): string => String(value || '').trim();

export function splitDisplayName(displayName?: string | null): { firstName: string; lastName: string } {
  const parts = clean(displayName).split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

export function providerIdForUser(user: User, fallback = ''): string {
  return clean(user.providerData?.[0]?.providerId) || fallback;
}

export function cacheAuthUser(user: User): void {
  localStorage.setItem('user', JSON.stringify({
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
    photoURL: user.photoURL,
    emailVerified: user.emailVerified,
  }));
}

export function clearCachedAuthUser(): void {
  localStorage.removeItem('user');
}

export function isEnsureUserBlockedError(error: unknown): boolean {
  const status = (error as { status?: number } | null | undefined)?.status;
  if (status === 409) return true;
  if (status === 403) {
    const message = error instanceof Error ? error.message.toLowerCase() : '';
    return (
      message.includes('email provider') ||
      message.includes('not allowed for new sarvalok accounts')
    );
  }
  return false;
}

export function ensureUserErrorMessage(
  error: unknown,
  fallback = 'This account cannot continue. Please sign in with your existing Sarvalok account.',
): string {
  if (error instanceof Error && error.message) return error.message;
  const detail = (error as { detail?: string } | null | undefined)?.detail;
  return detail || fallback;
}

export function needsUserDocRepair(data: Record<string, any> | null | undefined): boolean {
  if (!data) return true;
  if (!data.email || !data.subscription_name || data.subscription_id == null || data.period == null || data.active == null) {
    return true;
  }
  if (!Object.prototype.hasOwnProperty.call(data, 'hits')) return true;
  return false;
}

export function buildEnsureUserDocPayload(
  user: User,
  options: EnsureAuthUserDocOptions = {},
): EnsureUserDocPayload {
  const displayName = clean(options.displayName) || clean(user.displayName) || clean(user.email);
  const splitName = splitDisplayName(displayName);
  const deviceInfo = options.includeDeviceInfo === false ? {} : getSignupDeviceInfo();

  return {
    firstName: clean(options.firstName) || splitName.firstName,
    lastName: clean(options.lastName) || splitName.lastName,
    displayName,
    email: clean(user.email),
    provider: clean(options.provider) || providerIdForUser(user),
    source: clean(options.source) || 'frontend',
    ...deviceInfo,
  };
}

export async function ensureAuthUserDoc(
  user: User,
  options: EnsureAuthUserDocOptions = {},
): Promise<Record<string, any>> {
  const result = await ensureUserDocument(buildEnsureUserDocPayload(user, options));
  return result.user || {};
}
